import React, { FC, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  SvgIconProps,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBackRounded,
  NotificationsActiveOutlined,
  NotificationsNoneOutlined,
} from "@mui/icons-material";
import color from "../../Constants/colors";
import { DashboardMenu, DashboardMenuLabel } from "./DashboardMenu";
import SidebarProfile from "./SidebarProfile";
import { NavItem, useNavigationItems } from "./navigationItems";
import {
  useSidebarNavState,
  useSidebarSlot,
} from "../../Contexts/SidebarNavContext";

export const SIDEBAR_WIDTH = 256;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

// Panel outline with a chevron, pointing left to minimize and right to expand.
const PanelToggleIcon: FC<SvgIconProps & { expand?: boolean }> = ({
  expand,
  ...props
}) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <path d="M9 3.5v17" />
      <path
        d={expand ? "M13.5 9.5 16 12l-2.5 2.5" : "M16 9.5 13.5 12l2.5 2.5"}
      />
    </g>
  </SvgIcon>
);

// The main menu and a page's own navigation slide over each other.
const panelSx = (visible: boolean, side: "left" | "right") => ({
  position: "absolute",
  inset: 0,
  px: 0.75,
  display: "flex",
  flexDirection: "column",
  transform: visible
    ? "none"
    : `translateX(${side === "left" ? "-100%" : "100%"})`,
  opacity: visible ? 1 : 0,
  visibility: visible ? "visible" : "hidden",
  transition: `transform 0.25s ease, opacity 0.25s ease, visibility 0s linear ${
    visible ? "0s" : "0.25s"
  }`,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

const rowButtonSx = (collapsed: boolean) => ({
  minHeight: 40,
  borderRadius: "10px",
  px: collapsed ? 0 : 1.25,
  justifyContent: collapsed ? "center" : "flex-start",
  color: color.primary_dark,
  "&:hover": { backgroundColor: "rgba(3, 46, 77, 0.06)" },
});

interface Props {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;

  onLogout: () => void;
}

const Sidebar: FC<Props> = ({ collapsed, onToggle, onNavigate, onLogout }) => {
  const { t } = useTranslation("dashboard");
  const { main, bottom } = useNavigationItems();
  const sidebarSlot = useSidebarSlot();
  const contextNav = useSidebarNavState()?.contextNav ?? null;
  const mainPanelRef = useRef<HTMLDivElement | null>(null);
  const contextPanelRef = useRef<HTMLDivElement | null>(null);
  const contextPath = contextNav?.path ?? null;

  // A page with its own navigation opens on it; leaving the page shows the
  // main menu again. In between the user can switch back and forth.
  const [view, setView] = useState<"main" | "context">(
    contextPath ? "context" : "main",
  );
  const [viewForPath, setViewForPath] = useState(contextPath);
  if (viewForPath !== contextPath) {
    setViewForPath(contextPath);
    setView(contextPath ? "context" : "main");
  }
  const showContext = Boolean(contextNav) && view === "context";

  // Switching by hand moves focus into the panel that slides in, so keyboard
  // users don't stay on a button that just became hidden.
  const pendingFocusRef = useRef<"main" | "context" | null>(null);
  const switchView = (next: "main" | "context") => {
    pendingFocusRef.current = next;
    setView(next);
  };
  useEffect(() => {
    const target = pendingFocusRef.current;
    if (!target) return;
    pendingFocusRef.current = null;
    const panel =
      target === "main" ? mainPanelRef.current : contextPanelRef.current;
    panel
      ?.querySelector<HTMLElement>("a[href], button, input, [tabindex='0']")
      ?.focus();
  }, [view]);

  const mainItems = contextNav
    ? main.map((item) =>
        item.path === contextNav.path
          ? { ...item, drillIn: () => switchView("context") }
          : item,
      )
    : main;

  const toggleLabel = collapsed
    ? t("dashboard.expandPanel")
    : t("dashboard.minimizePanel");

  const toggleButton = (
    <ListItemButton
      onClick={onToggle}
      aria-label={toggleLabel}
      sx={rowButtonSx(collapsed)}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: "inherit" }}>
        <PanelToggleIcon expand={collapsed} sx={{ fontSize: 22 }} />
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={toggleLabel}
          primaryTypographyProps={{
            noWrap: true,
            sx: { fontSize: 14, fontWeight: 500, color: color.primary_dark },
          }}
        />
      )}
    </ListItemButton>
  );

  const backLabel = t("dashboard.mainMenu");
  const backButton = (
    <ListItemButton
      onClick={() => switchView("main")}
      aria-label={backLabel}
      sx={rowButtonSx(collapsed)}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: "inherit" }}>
        <ArrowBackRounded sx={{ fontSize: 22 }} />
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={backLabel}
          primaryTypographyProps={{
            noWrap: true,
            sx: { fontSize: 14, fontWeight: 500, color: color.primary_dark },
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <Box
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        px: collapsed ? 1 : 1.5,
        py: 2,
        borderRadius: "16px",
        backgroundColor: color.gray100,
        border: `1px solid ${color.gray200}`,
        overflow: "hidden",
        transition: "width 0.2s ease, padding 0.2s ease",
      }}
    >
      <Box
        component={Link}
        to="/home"
        onClick={onNavigate}
        aria-label="Connected Innovation Hub"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1,
          px: collapsed ? 0 : 0.5,
          minHeight: 48,
          textDecoration: "none",
        }}
      >
        <Box
          component="img"
          src="/commo_logo.png"
          alt=""
          sx={{ width: 44, height: 44, objectFit: "cover", flexShrink: 0 }}
        />
        {!collapsed && (
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              lineHeight: "17px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: color.primary_dark,
              whiteSpace: "nowrap",
            }}
          >
            Connected
            <br />
            Innovation Hub
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {collapsed ? (
          <Tooltip title={toggleLabel} placement="right" arrow>
            {toggleButton}
          </Tooltip>
        ) : (
          toggleButton
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          mx: -0.75,
          overflow: "hidden",
        }}
      >
        <Box
          ref={mainPanelRef}
          sx={panelSx(!showContext, "left")}
          aria-hidden={showContext}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              pt: 4,
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {!collapsed && (
              <DashboardMenuLabel>{t("dashboard.menu")}</DashboardMenuLabel>
            )}
            <DashboardMenu
              items={mainItems}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </Box>
          <Box sx={{ pt: 2 }}>
            {!collapsed && (
              <DashboardMenuLabel>{t("dashboard.profile")}</DashboardMenuLabel>
            )}
            <DashboardMenu
              items={[...bottom]}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          </Box>
        </Box>

        {contextNav && (
          <Box
            ref={contextPanelRef}
            sx={panelSx(showContext, "right")}
            aria-hidden={!showContext}
          >
            <Box sx={{ pt: 1 }}>
              {collapsed ? (
                <Tooltip title={backLabel} placement="right" arrow>
                  {backButton}
                </Tooltip>
              ) : (
                backButton
              )}
            </Box>
            {collapsed ? (
              <Divider sx={{ my: 1.5, mx: 1, borderColor: color.gray300 }} />
            ) : (
              <Box sx={{ pt: 3 }}>
                <DashboardMenuLabel>{contextNav.label}</DashboardMenuLabel>
              </Box>
            )}
            <Box
              ref={sidebarSlot?.setSlot}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ pt: 0.5 }}>
        <SidebarProfile
          collapsed={collapsed}
          onNavigate={onNavigate}
          onLogout={onLogout}
        />
      </Box>
    </Box>
  );
};

export default Sidebar;
